import { TEX } from "../core/keys"

export interface AnimationSpec {
  key: string
  texture: string
  assetPath: string
  frames: number
  frameRate: number
  repeat?: number
  frameWidth: number
  frameHeight: number
}

export const EXPLOSION_ANIMATIONS: Record<string, AnimationSpec> = {
  small: {
    key: "anim_explosion_small",
    texture: TEX.EXPLOSION_SMALL,
    assetPath: "explosions/explosion-1-a/spritesheet.webp",
    frames: 8,
    frameRate: 24,
    frameWidth: 32,
    frameHeight: 32,
  },
  medium: {
    key: "anim_explosion_medium",
    texture: TEX.EXPLOSION_MEDIUM,
    assetPath: "explosions/explosion-1-f/spritesheet.webp",
    frames: 8,
    frameRate: 20,
    frameWidth: 48,
    frameHeight: 48,
  },
  large: {
    key: "anim_explosion_large",
    texture: TEX.EXPLOSION_LARGE,
    assetPath: "explosions/explosion-1-b/spritesheet.webp",
    frames: 8,
    frameRate: 18,
    frameWidth: 64,
    frameHeight: 64,
  },
  debris: {
    key: "anim_explosion_debris",
    texture: TEX.EXPLOSION_DEBRIS,
    assetPath: "explosions/explosion-1-c/spritesheet.webp",
    frames: 10,
    frameRate: 22,
    frameWidth: 128,
    frameHeight: 80,
  },
  micro: {
    key: "anim_explosion_micro",
    texture: TEX.EXPLOSION_MICRO,
    assetPath: "explosions/explosion-1-g/spritesheet.webp",
    frames: 7,
    frameRate: 22,
    frameWidth: 48,
    frameHeight: 48,
  },
  asteroid: {
    key: "anim_asteroid_explode",
    texture: TEX.ASTEROID_EXPLODE,
    assetPath: "fx/void/asteroid-explode.webp",
    frames: 8,
    frameRate: 12,
    frameWidth: 96,
    frameHeight: 96,
  },
  neon: {
    key: "anim_explosion_neon",
    texture: TEX.EXPLOSION_NEON,
    assetPath: "explosions/explosion-neon.webp",
    frames: 9,
    frameRate: 20,
    frameWidth: 112,
    frameHeight: 128,
  },
}

export const PROJECTILE_ANIMATIONS: Record<string, AnimationSpec> = {
  bolt: {
    key: "anim_fx_bolt",
    texture: TEX.FX_BOLT,
    assetPath: "fx/bullets/bolt.webp",
    frames: 4,
    frameRate: 16,
    repeat: -1,
    frameWidth: 48,
    frameHeight: 32,
  },
  crossed: {
    key: "anim_fx_crossed",
    texture: TEX.FX_CROSSED,
    assetPath: "fx/bullets/crossed.webp",
    frames: 6,
    frameRate: 16,
    repeat: -1,
    frameWidth: 32,
    frameHeight: 32,
  },
  pulse: {
    key: "anim_fx_pulse",
    texture: TEX.FX_PULSE,
    assetPath: "fx/bullets/pulse.webp",
    frames: 4,
    frameRate: 16,
    repeat: -1,
    frameWidth: 63,
    frameHeight: 32,
  },
  charged: {
    key: "anim_fx_charged",
    texture: TEX.FX_CHARGED,
    assetPath: "fx/bullets/charged.webp",
    frames: 6,
    frameRate: 14,
    repeat: -1,
    frameWidth: 63,
    frameHeight: 48,
  },
  spark: {
    key: "anim_fx_spark",
    texture: TEX.FX_SPARK,
    assetPath: "fx/bullets/spark.webp",
    frames: 5,
    frameRate: 18,
    repeat: -1,
    frameWidth: 63,
    frameHeight: 32,
  },
  fireballBullet: {
    key: "anim_proj_fireball",
    texture: TEX.PROJ_FIREBALL,
    assetPath: "fx/bullets/fireball.webp",
    frames: 4,
    frameRate: 16,
    repeat: -1,
    frameWidth: 16,
    frameHeight: 16,
  },
  sparkBullet: {
    key: "anim_proj_spark",
    texture: TEX.PROJ_SPARK,
    assetPath: "fx/bullets/spark.webp",
    frames: 5,
    frameRate: 18,
    repeat: -1,
    frameWidth: 63,
    frameHeight: 32,
  },
  chargedBullet: {
    key: "anim_proj_charged",
    texture: TEX.PROJ_CHARGED,
    assetPath: "fx/bullets/charged.webp",
    frames: 6,
    frameRate: 14,
    repeat: -1,
    frameWidth: 63,
    frameHeight: 48,
  },
  waveform: {
    key: "anim_fx_waveform",
    texture: TEX.FX_WAVEFORM,
    assetPath: "fx/bullets/waveform.webp",
    frames: 4,
    frameRate: 20,
    repeat: -1,
    frameWidth: 95,
    frameHeight: 32,
  },
}

export const PROJECTILE_ANIMATION_BY_TEXTURE: Record<string, string> =
  Object.fromEntries(
    Object.values(PROJECTILE_ANIMATIONS).map((spec) => [
      spec.texture,
      spec.key,
    ]),
  )

export const HIT_ANIMATIONS: Record<string, AnimationSpec> = {
  hit1: {
    key: "anim_fx_hit_1",
    texture: TEX.FX_HIT_1,
    assetPath: "fx/hit/hit-1.webp",
    frames: 5,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
  hit2: {
    key: "anim_fx_hit_2",
    texture: TEX.FX_HIT_2,
    assetPath: "fx/hit/hit-2.webp",
    frames: 7,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
  hit3: {
    key: "anim_fx_hit_3",
    texture: TEX.FX_HIT_3,
    assetPath: "fx/hit/hit-3.webp",
    frames: 5,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
  hit4: {
    key: "anim_fx_hit_4",
    texture: TEX.FX_HIT_4,
    assetPath: "fx/hit/hit-4.webp",
    frames: 7,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
  hit5: {
    key: "anim_fx_hit_5",
    texture: TEX.FX_HIT_5,
    assetPath: "fx/hit/hit-5.webp",
    frames: 7,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
  hit6: {
    key: "anim_fx_hit_6",
    texture: TEX.FX_HIT_6,
    assetPath: "fx/hit/hit-6.webp",
    frames: 7,
    frameRate: 20,
    frameWidth: 32,
    frameHeight: 32,
  },
}

export const COIN_ANIMATIONS: Record<string, AnimationSpec> = {
  coin_s: {
    key: "anim_coin_s",
    texture: TEX.COIN_S,
    assetPath: "icons/coins/coin.webp",
    frames: 5,
    frameRate: 10,
    repeat: -1,
    frameWidth: 16,
    frameHeight: 16,
  },
  coin_m: {
    key: "anim_coin_m",
    texture: TEX.COIN_M,
    assetPath: "icons/coins/coin.webp",
    frames: 5,
    frameRate: 10,
    repeat: -1,
    frameWidth: 16,
    frameHeight: 16,
  },
  coin_l: {
    key: "anim_coin_l",
    texture: TEX.COIN_L,
    assetPath: "icons/coins/coin.webp",
    frames: 5,
    frameRate: 10,
    repeat: -1,
    frameWidth: 16,
    frameHeight: 16,
  },
}
